import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEO } from "@/components/SEO";
import { useJobPosting, useJobApplications } from "@/hooks/useMyJobs";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { 
  ArrowLeft, Users, Download, FileText, Mail, Phone, Calendar, Loader2, Inbox
} from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { format } from "date-fns";
import { it, enUS, es } from "date-fns/locale";
import type { Locale } from "date-fns";
import { useState } from "react";

const dateLocales: Record<string, Locale> = {
  it: it,
  en: enUS,
  es: es,
};

const JobApplications = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const langKey = (i18n.resolvedLanguage ?? i18n.language).split("-")[0];
  const currentLocale = dateLocales[langKey] || it;

  const { data: job, isLoading: loadingJob } = useJobPosting(id);
  const { data: applications, isLoading: loadingApplications } = useJobApplications(id);

  const [coverLetterModal, setCoverLetterModal] = useState<{ open: boolean; content: string; name: string }>({
    open: false,
    content: '',
    name: ''
  });

  const isLoading = loadingJob || loadingApplications;

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-surface">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </main>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen flex flex-col bg-surface">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-8">
          <div className="text-center py-16">
            <p className="text-muted-foreground">{t("jobs.applications.notFound")}</p>
            <Link to="/dashboard/company/jobs">
              <BrutalButton className="mt-4">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t("common.back")}
              </BrutalButton>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-surface">
      <SEO 
        title={`${t("jobs.applications.title")} - ${job.title} | Inclusia`}
        description={t("jobs.applications.subtitle")}
        noIndex={true}
      />
      <Header />

      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <Breadcrumb className="mb-4">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/dashboard">{t("common.dashboard")}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/dashboard/company/jobs">{t("jobs.manage.title")}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{t("jobs.applications.title")}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {/* Header */}
          <div className="flex items-center gap-4 mb-2">
            <button
              onClick={() => navigate("/dashboard/company/jobs")}
              className="p-2 hover:bg-accent transition-colors rounded-lg"
              aria-label={t("common.back")}
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="font-heading text-3xl font-bold text-dark">
              {t("jobs.applications.title")} - {job.title}
            </h1>
          </div>

          <p className="text-muted-foreground mb-8 ml-14">
            {t("jobs.applications.subtitle")}
          </p>

          {/* Applications List */}
          {applications && applications.length > 0 ? (
            <div className="space-y-4">
              {applications.map((app) => (
                <div 
                  key={app.id}
                  className="bg-card border-[3px] border-dark shadow-[4px_4px_0px_#101322] p-6"
                >
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="font-heading text-lg font-bold text-dark mb-2">
                        {app.applicant_name}
                      </h3>
                      <div className="space-y-2 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          <a href={`mailto:${app.applicant_email}`} className="hover:text-primary">
                            {app.applicant_email}
                          </a>
                        </div>
                        {app.applicant_phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4" />
                            <a href={`tel:${app.applicant_phone}`} className="hover:text-primary">
                              {app.applicant_phone}
                            </a>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>
                            {t("jobs.applications.appliedOn")} {format(new Date(app.applied_at || new Date()), 'd MMM yyyy, HH:mm', { locale: currentLocale })}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {app.cv_file_url && (
                        <a href={app.cv_file_url} target="_blank" rel="noopener noreferrer">
                          <BrutalButton variant="outline" size="sm">
                            <Download className="w-4 h-4 mr-2" />
                            {t("jobs.applications.downloadCv")}
                          </BrutalButton>
                        </a>
                      )}
                      {app.cover_letter && (
                        <BrutalButton 
                          variant="outline" 
                          size="sm"
                          onClick={() => setCoverLetterModal({
                            open: true,
                            content: app.cover_letter || '',
                            name: app.applicant_name
                          })}
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          {t("jobs.applications.viewCoverLetter")}
                        </BrutalButton>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-card border-[3px] border-dark">
              <Inbox className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h2 className="font-heading text-2xl font-bold mb-2 text-dark">
                {t("jobs.applications.noApplications")}
              </h2>
              <p className="text-muted-foreground">
                {t("jobs.applications.noApplicationsDesc")}
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />

      {/* Cover Letter Modal */}
      <Dialog open={coverLetterModal.open} onOpenChange={(open) => setCoverLetterModal(prev => ({ ...prev, open }))}>
        <DialogContent className="border-[3px] border-dark max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {t("jobs.applications.coverLetter")} - {coverLetterModal.name}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 whitespace-pre-wrap text-sm">
            {coverLetterModal.content}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobApplications;
